import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Button, ButtonIcon, SearchField, Text } from '@backstage/ui';
import { RiStarFill, RiStarLine } from '@remixicon/react';
import { SIDEBAR_NAV_GROUPS, SidebarNavItem } from './sidebarNavigation';

type SidebarMorePanelProps = {
  id: string;
  items: SidebarNavItem[];
  favorites: ReadonlySet<string>;
  activePath?: string;
  anchor: HTMLElement;
  left: number;
  onToggleFavorite: (item: SidebarNavItem) => void;
  onReset: () => void;
  onSearch: () => void;
  onClose: (restoreFocus: boolean) => void;
  onNavigate: (to: string) => void;
  /** Opened by a click rather than a hover preview: takes focus. */
  pinned: boolean;
  /** A click, focus or keypress inside a previewed panel: keep it open. */
  onEngage: () => void;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
};

/**
 * Every tool, grouped by job. Starring a tool adds it to the sidebar, which
 * keeps the sidebar short as more plugins arrive.
 */
export const SidebarMorePanel = ({
  id,
  items,
  favorites,
  activePath,
  anchor,
  left,
  onToggleFavorite,
  onReset,
  onSearch,
  onClose,
  onNavigate,
  pinned,
  onEngage,
  onPointerEnter,
  onPointerLeave,
}: SidebarMorePanelProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [bottom, setBottom] = useState(8);
  const [height, setHeight] = useState<number>();
  const filter = query.trim().toLocaleLowerCase('en-US');
  const filtering = filter !== '';

  // Open upwards from the More row, like the legacy flyout, and move higher
  // when the panel would otherwise run off the top of the window. A filtered
  // panel keeps the size and place of the full list, so nothing moves while
  // someone types.
  useLayoutEffect(() => {
    const place = () => {
      const panelHeight = ref.current?.getBoundingClientRect().height ?? 0;
      if (!filtering) setHeight(panelHeight);
      const fromRow =
        window.innerHeight - anchor.getBoundingClientRect().bottom;
      const fits = window.innerHeight - panelHeight - 8;
      setBottom(Math.max(8, Math.min(fromRow, fits)));
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [anchor, filtering]);

  // A hover preview must not steal focus from the page.
  useEffect(() => {
    if (pinned) ref.current?.querySelector<HTMLInputElement>('input')?.focus();
  }, [pinned]);

  useEffect(() => {
    const panel = ref.current;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose(true);
        return;
      }
      onEngage();
    };
    panel?.addEventListener('keydown', onKeyDown);
    panel?.addEventListener('pointerdown', onEngage);
    panel?.addEventListener('focusin', onEngage);
    return () => {
      panel?.removeEventListener('keydown', onKeyDown);
      panel?.removeEventListener('pointerdown', onEngage);
      panel?.removeEventListener('focusin', onEngage);
    };
  }, [onClose, onEngage]);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!ref.current?.contains(target) && !anchor.contains(target)) {
        onClose(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [anchor, onClose]);

  const groups = SIDEBAR_NAV_GROUPS.map(group => ({
    group,
    // Favourites keep the order people chose; the full list is alphabetical.
    items: items
      .filter(
        item =>
          item.group === group &&
          (!filter || item.title.toLocaleLowerCase('en-US').includes(filter)),
      )
      .sort((a, b) =>
        a.title.localeCompare(b.title, 'en', { sensitivity: 'base' }),
      ),
  })).filter(group => group.items.length > 0);

  return createPortal(
    <div
      ref={ref}
      id={id}
      className="make-sidebar-more"
      role="dialog"
      aria-label="All tools"
      style={{ left, bottom, height: filtering ? height : undefined }}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      <div className="make-sidebar-more__header">
        <Text as="h2" variant="title-x-small" weight="bold">
          All tools
        </Text>
        <Text as="p" variant="body-small" color="secondary">
          Star a tool to add it to your sidebar. Drag sidebar items to reorder
          them.
        </Text>
        <SearchField
          aria-label="Filter tools"
          placeholder="Filter tools"
          size="small"
          value={query}
          onChange={setQuery}
        />
      </div>
      <div className="make-sidebar-more__body">
        {groups.length === 0 && (
          <Text as="p" variant="body-small" color="secondary">
            No tools match “{query}”.
          </Text>
        )}
        {groups.map(({ group, items: groupItems }) => (
          <section
            key={group}
            className="make-sidebar-more__group"
            aria-label={group}
          >
            <Text
              as="h3"
              variant="body-x-small"
              color="secondary"
              className="make-sidebar-more__group-title"
            >
              {group}
            </Text>
            {groupItems.map(item => {
              const Icon = item.icon;
              const inSidebar = favorites.has(item.path);
              const content = (
                <>
                  <Icon size={18} aria-hidden />
                  <span>{item.title}</span>
                </>
              );
              return (
                <div key={item.path} className="make-sidebar-more__row">
                  {item.path === '/search' ? (
                    <button
                      type="button"
                      className="make-sidebar-more__link make-sidebar-more__action"
                      onClick={onSearch}
                    >
                      {content}
                    </button>
                  ) : (
                    <Link
                      to={item.path}
                      className="make-sidebar-more__link"
                      aria-current={
                        activePath === item.path ? 'page' : undefined
                      }
                      onClick={() => onNavigate(item.path)}
                    >
                      {content}
                    </Link>
                  )}
                  <ButtonIcon
                    size="small"
                    variant="tertiary"
                    className={
                      inSidebar ? 'make-sidebar-more__star--on' : undefined
                    }
                    icon={inSidebar ? <RiStarFill /> : <RiStarLine />}
                    aria-label={
                      inSidebar
                        ? `Remove ${item.title} from sidebar`
                        : `Add ${item.title} to sidebar`
                    }
                    aria-pressed={inSidebar}
                    onPress={() => onToggleFavorite(item)}
                  />
                </div>
              );
            })}
          </section>
        ))}
      </div>
      <div className="make-sidebar-more__footer">
        <Text as="span" variant="body-x-small" color="secondary">
          {favorites.size} in your sidebar
        </Text>
        <Button size="small" variant="tertiary" onPress={onReset}>
          Reset to default
        </Button>
      </div>
    </div>,
    document.body,
  );
};
